import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useTranslation } from 'react-i18next';

import { type Board, readRecentBoardIds, useBoards, useMoveBoard } from '@/entities/board';
import {
  activeFilterCount,
  type CatalogFilters,
  effectiveSort,
  type Filterable,
  labelMatches,
  labelRefs,
  matchesFilters,
  type ResourceKind,
} from '@/entities/catalog';
import {
  type Database,
  dbCan,
  readRecentDatabaseIds,
  useMoveDatabase,
  useWorkspaceDatabases,
} from '@/entities/database';
import {
  DOCUMENT_PERMISSIONS,
  documentCan,
  useDocumentSearch,
  useWorkspaceDocuments,
  type WorkspaceDocument,
} from '@/entities/document';
import {
  buildTree,
  type CardMenuAction,
  type DraggedResource,
  groupOf,
  pathLabel,
  pathOf,
  resourceOf,
  type Section,
  type SectionNode,
  useMoveSection,
  usePlaceResource,
  useSections,
} from '@/entities/section';
import {
  type GroupChild,
  groupAddress,
  groupPath,
  useCreateGroup,
  useGroups,
  useMoveGroup,
  type WorkspaceGroup,
} from '@/entities/workspace-group';
import { useWorkspace } from '@/entities/workspace';
import { useErrorMessage } from '@/shared/i18n';
import { BoardTile } from '@/features/board-manage';
import { DatabaseCard } from '@/features/database-catalog';
import { DocumentCard } from '@/features/document-catalog';
import { useCopyLink } from '@/features/resource-nav';
import {
  DissolveGroupDialog,
  GroupCard,
  GroupLabelsDialog,
  GroupNameDialog,
} from '@/features/workspace-groups';
import {
  DeleteSectionDialog,
  type MoveSubject,
  MoveToSectionDialog,
  SectionNameDialog,
} from '@/features/workspace-sections';
import { EmptyState, ErrorState, Icons, LoadingState, useToast } from '@/shared/ui';

import styles from './WorkspacePage.module.css';
import { SectionTree } from './SectionTree';
import { WORKSPACE_OFFERED, WORKSPACE_SORTS } from './workspace-filters';

interface Entry {
  key: string;
  kind: ResourceKind;
  name: string;
  /** Position in the manual order: boards first, then databases, each in its own sequence. */
  manual: number;
  rank: number;
  filterable: Filterable;
  node: ReactNode;
}

// --- which sections the viewer collapsed: a personal UI preference of this browser ---------------

const collapsedKey = (workspaceId: string) => `planosfera.sections.collapsed.${workspaceId}`;
const listeners = new Set<() => void>();

function readCollapsed(workspaceId: string): string {
  try {
    return window.localStorage.getItem(collapsedKey(workspaceId)) ?? '[]';
  } catch {
    return '[]';
  }
}

/** Collapsing never touches the server: it is remembered in this browser only. */
function useCollapsed(workspaceId: string) {
  const raw = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    () => readCollapsed(workspaceId),
    () => '[]',
  );
  const collapsed = useMemo<ReadonlySet<string>>(() => {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return new Set(Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : []);
    } catch {
      return new Set();
    }
  }, [raw]);
  const toggle = useCallback(
    (id: string) => {
      const next = new Set(collapsed);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        window.localStorage.setItem(collapsedKey(workspaceId), JSON.stringify([...next]));
      } catch {
        // Storage unavailable: the state simply is not remembered.
      }
      for (const listener of listeners) listener();
    },
    [collapsed, workspaceId],
  );
  return { collapsed, toggle };
}

function findNode(nodes: readonly SectionNode[], id: string): SectionNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const inside = findNode(node.children, id);
    if (inside) return inside;
  }
  return null;
}

/** While a card is dragged inside an open group: drop here to take it one level up. */
function OutOfGroupDrop({
  workspaceId,
  onDrop,
}: {
  workspaceId: string;
  onDrop: (source: Record<string | symbol, unknown>) => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [over, setOver] = useState(false);
  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) =>
          resourceOf(source.data) !== null || groupOf(source.data) !== null,
        onDragStart: () => {
          setDragging(true);
        },
        onDrop: () => {
          setDragging(false);
        },
      }),
    [],
  );
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    return dropTargetForElements({
      element,
      canDrop: ({ source }) => {
        const dropped = resourceOf(source.data) ?? groupOf(source.data);
        return dropped?.workspaceId === workspaceId;
      },
      onDragEnter: () => {
        setOver(true);
      },
      onDragLeave: () => {
        setOver(false);
      },
      onDrop: ({ source }) => {
        setOver(false);
        onDrop(source.data);
      },
    });
  }, [workspaceId, onDrop, dragging]);
  if (!dragging) return null;
  return (
    <div
      ref={ref}
      className={styles.groupOutDrop}
      data-over={over || undefined}
      data-testid="group-out-drop"
    >
      <Icons.chevronLeft /> {t('groups.dropOut')}
    </div>
  );
}

/** "Workspace / Development / Backend": every ancestor navigates, the last one is the page. */
function Breadcrumbs({
  sections,
  focus,
  onFocus,
  groups = [],
  onOpenGroup,
}: {
  sections: readonly Section[];
  focus: SectionNode | null;
  onFocus: (id: string | null) => void;
  /** The open group's path after the section's; its last entry is the page. */
  groups?: readonly WorkspaceGroup[];
  onOpenGroup?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const trail = focus ? pathOf(sections, focus.id) : [];
  return (
    <nav className={styles.sectionCrumbs} aria-label={t('sections.pathLabel')}>
      <ol>
        <li>
          <button
            type="button"
            onClick={() => {
              onFocus(null);
            }}
          >
            {t('sections.root')}
          </button>
        </li>
        {trail.map((section, index) => (
          <li key={section.id}>
            <span aria-hidden className={styles.crumbSeparator}>
              /
            </span>
            {index === trail.length - 1 && groups.length === 0 ? (
              <span aria-current="page" title={section.name}>
                {section.name}
              </span>
            ) : (
              <button
                type="button"
                title={section.name}
                onClick={() => {
                  onFocus(section.id);
                }}
              >
                {section.name}
              </button>
            )}
          </li>
        ))}
        {groups.map((group, index) => (
          <li key={group.id}>
            <span aria-hidden className={styles.crumbSeparator}>
              /
            </span>
            {index === groups.length - 1 ? (
              <span aria-current="page" title={group.name} data-testid="group-crumb-current">
                {group.name}
              </span>
            ) : (
              <button
                type="button"
                title={group.name}
                data-testid="group-crumb"
                onClick={() => {
                  onOpenGroup?.(group.id);
                }}
              >
                {group.name}
              </button>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/**
 * The accessible boards and databases of ONE workspace, narrowed by the shared search + filters.
 *
 * Without a search, filter or other sort the catalog is the section HIERARCHY (collapsible
 * branches, a breadcrumb for an open section) and a card can be filed by dragging or from its menu.
 * With any search / filter / sort the list is FLAT across every depth - a resource in a collapsed
 * section is still found, and each result says where it is filed - and nothing can be reordered
 * (a filtered list never defines the canonical order). Objects the viewer cannot reach are absent
 * from the sources (the server never lists them), so neither the tree nor a path can reveal them.
 * Boards and databases keep two independent manual sequences per container.
 */
export function WorkspaceObjects({
  workspaceId,
  kinds,
  query,
  filters,
  locale,
  userId,
  focusId,
  onFocus,
  groupId,
  onOpenGroup,
}: {
  workspaceId: string;
  kinds: readonly ResourceKind[];
  query: string;
  filters: CatalogFilters;
  locale: string;
  userId: string | null;
  /** The open section (breadcrumb view); `null` = the workspace root. */
  focusId: string | null;
  onFocus: (id: string | null) => void;
  /** The open group (its contents are the page); `null` = none. */
  groupId: string | null;
  onOpenGroup: (id: string | null, sectionId?: string | null) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const copyLink = useCopyLink();
  const message = useErrorMessage();
  const boards = useBoards(workspaceId);
  const databases = useWorkspaceDatabases(workspaceId);
  const documents = useWorkspaceDocuments(workspaceId, kinds.includes('document'));
  const contentHits = useDocumentSearch(workspaceId, query);
  const sections = useSections(workspaceId);
  const groups = useGroups(workspaceId);
  const createGroup = useCreateGroup(workspaceId);
  const moveGroup = useMoveGroup(workspaceId);
  const place = usePlaceResource(workspaceId);
  const moveBoard = useMoveBoard(workspaceId);
  const moveDatabase = useMoveDatabase(workspaceId);
  const moveSection = useMoveSection(workspaceId);
  const { collapsed, toggle } = useCollapsed(workspaceId);
  const [naming, setNaming] = useState<{ parent: Section | null; section?: Section } | null>(null);
  const [deleting, setDeleting] = useState<Section | null>(null);
  const [moving, setMoving] = useState<MoveSubject | null>(null);
  const [namingGroup, setNamingGroup] = useState<WorkspaceGroup | null>(null);
  const [dissolving, setDissolving] = useState<WorkspaceGroup | null>(null);
  const [labelling, setLabelling] = useState<WorkspaceGroup | null>(null);
  // Labels exist in team workspaces only.
  const isTeamSpace = useWorkspace(workspaceId).data?.workspace_type === 'team';
  const offered = WORKSPACE_OFFERED;
  const sort = effectiveSort(filters, WORKSPACE_SORTS, 'manual');
  const narrowed =
    sort !== 'manual' || query.trim() !== '' || activeFilterCount(filters, offered) > 0;
  const sectionItems = useMemo(() => sections.data?.items ?? [], [sections.data]);
  const canManage = sections.data?.can_manage ?? false;
  const maxDepth = sections.data?.max_depth ?? 6;
  const roots = useMemo(() => buildTree(sectionItems), [sectionItems]);
  const focus = !narrowed && focusId ? findNode(roots, focusId) : null;
  const groupItems = useMemo(() => groups.data?.items ?? [], [groups.data]);
  const mayGroup = groups.data?.can_manage ?? false;
  const filing = sectionItems.length > 0 || groupItems.length > 0;
  const groupKnown = useMemo(() => new Set(groupItems.map((g) => g.id)), [groupItems]);
  const openGroup =
    !narrowed && groupId ? (groupItems.find((g) => g.id === groupId) ?? null) : null;

  const boardList: readonly Board[] = useMemo(
    () => (kinds.includes('board') ? (boards.data ?? []) : []),
    [boards.data, kinds],
  );
  const databaseList: readonly Database[] = useMemo(
    () => (kinds.includes('database') ? (databases.data ?? []) : []),
    [databases.data, kinds],
  );

  const documentList: readonly WorkspaceDocument[] = useMemo(
    () => (kinds.includes('document') ? (documents.data ?? []) : []),
    [documents.data, kinds],
  );

  /** What is directly inside a group, in the order of a container, as far as this viewer knows. */
  const childrenOf = useMemo(() => {
    const map = new Map<string, GroupChild[]>();
    const add = (group: string | null | undefined, child: GroupChild) => {
      if (group) map.set(group, [...(map.get(group) ?? []), child]);
    };
    for (const group of groupItems) {
      add(group.parent_group_id, { kind: 'group', id: group.id, name: group.name });
    }
    for (const board of boardList) {
      add(board.group_id, { kind: 'board', id: board.id, name: board.name });
    }
    for (const database of databaseList) {
      add(database.group_id, { kind: 'database', id: database.id, name: database.name });
    }
    for (const document of documentList) {
      add(document.group_id, { kind: 'document', id: document.id, name: document.name });
    }
    return map;
  }, [groupItems, boardList, databaseList, documentList]);

  /**
   * In a tab narrowed to one type a group stays only if that type is somewhere inside it, and in
   * the previews its child groups follow the same rule, so no group looks empty by accident.
   */
  const showable = useMemo(() => {
    const yes = new Set<string>();
    const visit = (id: string, seen: ReadonlySet<string>): boolean => {
      if (yes.has(id)) return true;
      if (seen.has(id)) return false;
      const found = (childrenOf.get(id) ?? []).some(
        (child) => child.kind !== 'group' || visit(child.id, new Set([...seen, id])),
      );
      if (found) yes.add(id);
      return found;
    };
    for (const group of groupItems) visit(group.id, new Set());
    return yes;
  }, [childrenOf, groupItems]);
  const allKinds = kinds.length === 3;
  const worthShowing = useCallback(
    (id: string) => allKinds || showable.has(id),
    [allKinds, showable],
  );

  const fail = useCallback(
    (error: unknown) => {
      toast.show({ message: message(error) });
    },
    [toast, message],
  );
  const onDropResource = useCallback(
    (kind: 'board' | 'database' | 'document', id: string, sectionId: string | null) => {
      if (place.isPending) return;
      place.mutate({ kind, id, section_id: sectionId }, { onError: fail });
    },
    [place, fail],
  );
  const onDropSection = useCallback(
    (
      id: string,
      parentId: string | null,
      anchor?: { after_id: string } | { before_id: string },
    ) => {
      if (moveSection.isPending) return;
      moveSection.mutate({ id, parent_id: parentId, ...anchor }, { onError: fail });
    },
    [moveSection, fail],
  );

  const onDropGroup = useCallback(
    (id: string, sectionId: string | null) => {
      if (moveGroup.isPending) return;
      moveGroup.mutate({ id, section_id: sectionId, parent_group_id: null }, { onError: fail });
    },
    [moveGroup, fail],
  );
  /** File a resource directly inside a group. */
  const fileInGroup = useCallback(
    (source: DraggedResource, group: WorkspaceGroup) => {
      place.mutate(
        { kind: source.kind, id: source.id, section_id: group.section_id, group_id: group.id },
        { onError: fail },
      );
    },
    [place, fail],
  );
  /** One level up from a group: its parent group, or directly in its section. */
  const moveOutOf = useCallback(
    (group: WorkspaceGroup, source: Record<string | symbol, unknown>) => {
      const resource = resourceOf(source);
      if (resource) {
        place.mutate(
          {
            kind: resource.kind,
            id: resource.id,
            section_id: group.section_id,
            ...(group.parent_group_id && { group_id: group.parent_group_id }),
          },
          { onError: fail },
        );
        return;
      }
      const dragged = groupOf(source);
      if (dragged && dragged.id !== group.id) {
        moveGroup.mutate(
          {
            id: dragged.id,
            section_id: group.section_id,
            parent_group_id: group.parent_group_id,
          },
          { onError: fail },
        );
      }
    },
    [place, moveGroup, fail],
  );

  // a resource whose section this viewer's list does not know is shown at the root
  const known = useMemo(() => new Set(sectionItems.map((s) => s.id)), [sectionItems]);
  const containerOf = (id: string | null | undefined) => (id && known.has(id) ? id : null);

  /** Group actions of one resource card: drop-to-group, "new group", "take out of the group". */
  const groupingOf = (
    kind: DraggedResource['kind'],
    resource: { id: string; name: string; section_id?: string | null; group_id?: string | null },
  ): { onCombine?: (source: DraggedResource) => void; menu: CardMenuAction[] } => {
    const section = resource.section_id ?? null;
    const group = resource.group_id ?? null;
    const menu: CardMenuAction[] = [];
    if (mayGroup) {
      menu.push({
        id: 'new-group',
        label: t('groups.newWith'),
        icon: <Icons.group />,
        onSelect: () => {
          createGroup.mutate(
            {
              name: t('groups.defaultName'),
              section_id: section,
              parent_group_id: group,
              members: [{ kind, id: resource.id }],
            },
            { onError: fail },
          );
        },
      });
    }
    const holder = group ? groupItems.find((g) => g.id === group) : undefined;
    if (holder) {
      menu.push({
        id: 'move-out',
        label: t('groups.moveOut'),
        icon: <Icons.chevronLeft />,
        onSelect: () => {
          place.mutate(
            {
              kind,
              id: resource.id,
              section_id: holder.section_id,
              ...(holder.parent_group_id && { group_id: holder.parent_group_id }),
            },
            { onError: fail },
          );
        },
      });
    }
    return {
      ...(mayGroup && {
        onCombine: (source: DraggedResource) => {
          if (createGroup.isPending) return;
          createGroup.mutate(
            {
              name: t('groups.defaultName'),
              section_id: section,
              parent_group_id: group,
              members: [
                { kind, id: resource.id },
                { kind: source.kind, id: source.id },
              ],
            },
            {
              onError: fail,
              onSuccess: (created) => {
                toast.show({ message: t('groups.created', { name: created.name }) });
              },
            },
          );
        },
      }),
      menu,
    };
  };

  const documentCard = (
    document: WorkspaceDocument,
    siblings: readonly WorkspaceDocument[],
    index: number,
    location?: string,
  ) => {
    const mayEdit = documentCan(document, DOCUMENT_PERMISSIONS.editDetails);
    const grouping = !narrowed && mayEdit ? groupingOf('document', document) : undefined;
    return (
      <DocumentCard
        key={document.id}
        document={document}
        location={location}
        previous={siblings[index - 1]}
        next={siblings[index + 1]}
        onCombine={grouping?.onCombine}
        groupMenu={grouping?.menu}
        onDropBeside={
          !narrowed
            ? (sourceId, anchor) => {
                place.mutate(
                  {
                    kind: 'document',
                    id: sourceId,
                    section_id: document.section_id,
                    ...(document.group_id && { group_id: document.group_id }),
                    ...anchor,
                  },
                  { onError: fail },
                );
              }
            : undefined
        }
        onMoveToSection={
          mayEdit && filing
            ? () => {
                setMoving({
                  kind: 'document',
                  id: document.id,
                  name: document.name,
                  sectionId: document.section_id,
                  groupId: document.group_id,
                });
              }
            : undefined
        }
      />
    );
  };

  const groupCard = (group: WorkspaceGroup, location?: string) => (
    <GroupCard
      key={group.id}
      group={group}
      items={(childrenOf.get(group.id) ?? []).filter(
        (child) => child.kind !== 'group' || worthShowing(child.id),
      )}
      location={location}
      actions={{
        onOpen: () => {
          onOpenGroup(group.id, group.section_id);
        },
        onCopyLink: () => {
          copyLink(groupAddress(group));
        },
        ...(mayGroup &&
          isTeamSpace && {
            onLabels: () => {
              setLabelling(group);
            },
          }),
        ...(mayGroup && {
          onRename: () => {
            setNamingGroup(group);
          },
          onMove: () => {
            setMoving({
              kind: 'group',
              id: group.id,
              name: group.name,
              sectionId: group.section_id,
              groupId: group.parent_group_id,
            });
          },
          onDissolve: () => {
            setDissolving(group);
          },
        }),
        ...(mayGroup &&
          group.parent_group_id && {
            onMoveOut: () => {
              const parent = groupItems.find((g) => g.id === group.parent_group_id);
              moveGroup.mutate(
                {
                  id: group.id,
                  section_id: group.section_id,
                  parent_group_id: parent?.parent_group_id ?? null,
                },
                { onError: fail },
              );
            },
          }),
        onDropResource: (source) => {
          fileInGroup(source, group);
        },
        ...(mayGroup && {
          onDropGroup: (id, anchor) => {
            moveGroup.mutate(
              anchor
                ? {
                    id,
                    section_id: group.section_id,
                    parent_group_id: group.parent_group_id,
                    ...anchor,
                  }
                : { id, section_id: group.section_id, parent_group_id: group.id },
              { onError: fail },
            );
          },
        }),
      }}
    />
  );

  const boardTile = (
    board: Board,
    siblings: readonly Board[],
    index: number,
    container: string | null,
    location?: string,
  ) => {
    const mayEdit = board.can_edit_details === true;
    const grouping = !narrowed && mayEdit ? groupingOf('board', board) : undefined;
    const order =
      !narrowed && mayEdit
        ? {
            previous: siblings[index - 1],
            next: siblings[index + 1],
            pending: place.isPending || moveBoard.isPending,
            // inside its own container it is the plain reorder; from another one it is a filing
            onMove: (anchor: { after_id: string } | { before_id: string }) => {
              moveBoard.mutate({ id: board.id, ...anchor }, { onError: fail });
            },
            onDropBeside: (
              sourceId: string,
              anchor: { after_id: string } | { before_id: string },
            ) => {
              const source = boardList.find((b) => b.id === sourceId);
              if (
                source &&
                containerOf(source.section_id) === container &&
                (source.group_id ?? null) === (board.group_id ?? null)
              ) {
                moveBoard.mutate({ id: sourceId, ...anchor }, { onError: fail });
              } else {
                place.mutate(
                  {
                    kind: 'board',
                    id: sourceId,
                    section_id: container,
                    ...(board.group_id && { group_id: board.group_id }),
                    ...anchor,
                  },
                  { onError: fail },
                );
              }
            },
          }
        : undefined;
    return (
      <BoardTile
        key={board.id}
        board={board}
        order={order}
        location={location}
        onCombine={grouping?.onCombine}
        groupMenu={grouping?.menu}
        onMoveToSection={
          mayEdit && filing
            ? () => {
                setMoving({
                  kind: 'board',
                  id: board.id,
                  name: board.name,
                  sectionId: board.section_id ?? null,
                  groupId: board.group_id ?? null,
                });
              }
            : undefined
        }
      />
    );
  };
  const databaseCard = (
    database: Database,
    siblings: readonly Database[],
    index: number,
    container: string | null,
    location?: string,
  ) => (
    <DatabaseCard
      key={database.id}
      database={database}
      orderable={!narrowed}
      previous={siblings[index - 1]}
      next={siblings[index + 1]}
      location={location}
      onDropBeside={
        !narrowed
          ? (sourceId, anchor) => {
              const source = databaseList.find((d) => d.id === sourceId);
              if (
                source &&
                containerOf(source.section_id) === container &&
                (source.group_id ?? null) === (database.group_id ?? null)
              ) {
                moveDatabase.mutate({ id: sourceId, ...anchor }, { onError: fail });
              } else {
                place.mutate(
                  {
                    kind: 'database',
                    id: sourceId,
                    section_id: container,
                    ...(database.group_id && { group_id: database.group_id }),
                    ...anchor,
                  },
                  { onError: fail },
                );
              }
            }
          : undefined
      }
      onCombine={
        !narrowed && dbCan(database, 'database.edit_details')
          ? groupingOf('database', database).onCombine
          : undefined
      }
      groupMenu={
        !narrowed && dbCan(database, 'database.edit_details')
          ? groupingOf('database', database).menu
          : undefined
      }
      onMoveToSection={
        dbCan(database, 'database.edit_details') && filing
          ? () => {
              setMoving({
                kind: 'database',
                id: database.id,
                name: database.name,
                sectionId: database.section_id ?? null,
                groupId: database.group_id ?? null,
              });
            }
          : undefined
      }
    />
  );

  const flat = useMemo(() => {
    const opened = new Map((userId ? readRecentBoardIds(userId) : []).map((id, i) => [id, i]));
    const openedDatabases = new Map(
      (userId ? readRecentDatabaseIds(userId) : []).map((id, i) => [id, i]),
    );
    const openedDocuments = new Map<string, number>();
    const where = (section: string | null | undefined, group?: string | null) => {
      const parts = [
        ...(section ? [pathLabel(sectionItems, section)] : []),
        ...groupPath(groupItems, group).map((g) => g.name),
      ];
      return parts.length > 0 ? parts.join(' / ') : undefined;
    };
    const matchedText = new Set((contentHits.data ?? []).map((hit) => hit.id));
    const needle = query.trim().toLocaleLowerCase();
    const entries: Entry[] = [
      ...boardList.map((board, index): Entry => ({
        key: `board:${board.id}`,
        kind: 'board',
        name: board.name,
        manual: index,
        rank: opened.get(board.id) ?? Number.MAX_SAFE_INTEGER,
        filterable: {
          kind: 'board',
          favorite: board.is_favorite,
          recent: opened.has(board.id),
          ownerId: board.owner_user_id,
          space: null,
          haystack: [
            board.name.toLocaleLowerCase(),
            ...(board.labels ?? []).map((label) => label.name.toLocaleLowerCase()),
          ],
          labels: labelRefs(board.labels),
        },
        node: boardTile(board, [], index, null, where(board.section_id, board.group_id)),
      })),
      ...databaseList.map((database, index): Entry => ({
        key: `database:${database.id}`,
        kind: 'database',
        name: database.name,
        manual: boardList.length + index,
        rank: openedDatabases.get(database.id) ?? Number.MAX_SAFE_INTEGER,
        filterable: {
          kind: 'database',
          favorite: database.is_favorite,
          recent: openedDatabases.has(database.id),
          ownerId: database.owner_user_id,
          space: null,
          haystack: [
            database.name.toLocaleLowerCase(),
            ...database.labels.map((label) => label.name.toLocaleLowerCase()),
          ],
          labels: labelRefs(database.labels),
        },
        node: databaseCard(
          database,
          [],
          index,
          null,
          where(database.section_id, database.group_id),
        ),
      })),
      ...documentList.map((document, index): Entry => ({
        key: `document:${document.id}`,
        kind: 'document',
        name: document.name,
        manual: boardList.length + databaseList.length + index,
        rank: openedDocuments.get(document.id) ?? Number.MAX_SAFE_INTEGER,
        filterable: {
          kind: 'document',
          favorite: document.is_favorite,
          recent: false,
          ownerId: document.owner_user_id,
          space: null,
          // the server said its text matches: the search hit counts like a name match
          haystack: [
            document.name.toLocaleLowerCase(),
            ...(document.labels ?? []).map((label) => label.name.toLocaleLowerCase()),
            ...(matchedText.has(document.id) ? [needle] : []),
          ],
          labels: labelRefs(document.labels),
        },
        node: documentCard(document, [], index, where(document.section_id, document.group_id)),
      })),
    ];
    return entries
      .filter((entry) => matchesFilters(entry.filterable, filters, userId, query, offered))
      .sort((a, b) =>
        sort === 'name'
          ? a.name.localeCompare(b.name, locale) || a.manual - b.manual
          : sort === 'opened'
            ? a.rank - b.rank || a.manual - b.manual
            : a.manual - b.manual,
      );
    // the card builders only close over stable callbacks and the section list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    boardList,
    databaseList,
    documentList,
    contentHits.data,
    groupItems,
    filters,
    query,
    sort,
    locale,
    userId,
    sectionItems,
    filing,
    place.isPending,
  ]);

  // a resource whose group this viewer's list does not know is shown at its section's level
  const inNoGroup = (group: string | null | undefined) => !group || !groupKnown.has(group);
  const resourcesOf = (container: string | null): ReactNode[] => {
    const bs = boardList.filter(
      (b) => containerOf(b.section_id) === container && inNoGroup(b.group_id),
    );
    const dbs = databaseList.filter(
      (d) => containerOf(d.section_id) === container && inNoGroup(d.group_id),
    );
    const docs = documentList.filter(
      (d) => containerOf(d.section_id) === container && inNoGroup(d.group_id),
    );
    return [
      ...groupItems
        .filter(
          (g) =>
            g.parent_group_id === null &&
            containerOf(g.section_id) === container &&
            worthShowing(g.id),
        )
        .map((group) => groupCard(group)),
      ...bs.map((board, index) => boardTile(board, bs, index, container)),
      ...dbs.map((database, index) => databaseCard(database, dbs, index, container)),
      ...docs.map((document, index) => documentCard(document, docs, index)),
    ];
  };

  /** The contents of an open group, in the order of any container. */
  const contentsOf = (group: WorkspaceGroup): ReactNode[] => {
    const bs = boardList.filter((b) => b.group_id === group.id);
    const dbs = databaseList.filter((d) => d.group_id === group.id);
    const docs = documentList.filter((d) => d.group_id === group.id);
    const container = group.section_id;
    return [
      ...groupItems
        .filter((g) => g.parent_group_id === group.id && worthShowing(g.id))
        .map((child) => groupCard(child)),
      ...bs.map((board, index) => boardTile(board, bs, index, container)),
      ...dbs.map((database, index) => databaseCard(database, dbs, index, container)),
      ...docs.map((document, index) => documentCard(document, docs, index)),
    ];
  };

  const wanted = [
    ...(kinds.includes('board') ? [boards] : []),
    ...(kinds.includes('database') ? [databases] : []),
    ...(kinds.includes('document') ? [documents] : []),
  ];
  const failed = wanted.find((source) => source.isError);
  if (wanted.some((source) => source.isPending)) return <LoadingState />;
  if (failed) return <ErrorState error={failed.error} onRetry={() => void failed.refetch()} />;
  const nothing = boardList.length + databaseList.length + documentList.length === 0;
  if (nothing && sectionItems.length === 0 && groupItems.length === 0) {
    return <EmptyState title={t('workspace.objectsEmpty')} />;
  }

  const dialogs = (
    <>
      {naming && (
        <SectionNameDialog
          workspaceId={workspaceId}
          parent={naming.parent}
          section={naming.section}
          onClose={() => {
            setNaming(null);
          }}
        />
      )}
      {deleting && (
        <DeleteSectionDialog
          workspaceId={workspaceId}
          section={deleting}
          looksEmpty={
            !sectionItems.some((s) => s.parent_id === deleting.id) &&
            !boardList.some((b) => b.section_id === deleting.id) &&
            !databaseList.some((d) => d.section_id === deleting.id) &&
            !documentList.some((d) => d.section_id === deleting.id) &&
            !groupItems.some((g) => g.section_id === deleting.id)
          }
          onClose={() => {
            if (focusId === deleting.id) onFocus(deleting.parent_id);
            setDeleting(null);
          }}
        />
      )}
      {moving && (
        <MoveToSectionDialog
          workspaceId={workspaceId}
          subject={moving}
          sections={sectionItems}
          maxDepth={maxDepth}
          groups={groupItems}
          maxGroupDepth={groups.data?.max_depth ?? 8}
          onClose={() => {
            setMoving(null);
          }}
        />
      )}
      {namingGroup && (
        <GroupNameDialog
          workspaceId={workspaceId}
          group={namingGroup}
          onClose={() => {
            setNamingGroup(null);
          }}
        />
      )}
      {labelling && (
        <GroupLabelsDialog
          workspaceId={workspaceId}
          group={labelling}
          onClose={() => {
            setLabelling(null);
          }}
        />
      )}
      {dissolving && (
        <DissolveGroupDialog
          workspaceId={workspaceId}
          group={dissolving}
          onClose={() => {
            setDissolving(null);
          }}
          onDone={() => {
            if (groupId === dissolving.id) {
              onOpenGroup(dissolving.parent_group_id, dissolving.section_id);
            }
          }}
        />
      )}
    </>
  );

  if (narrowed) {
    const needle = query.trim().toLocaleLowerCase();
    const matchingSections =
      needle === '' ? [] : sectionItems.filter((s) => s.name.toLocaleLowerCase().includes(needle));
    // A group is found by its name and/or by ITS OWN labels (OR inside the labels, AND with the
    // name); what it holds is never matched through it.
    const byLabel = offered.label === true && filters.labels.length > 0 && isTeamSpace;
    const matchingGroups =
      needle === '' && !byLabel
        ? []
        : groupItems.filter(
            (g) =>
              (needle === '' || g.name.toLocaleLowerCase().includes(needle)) &&
              (!byLabel || labelMatches(filters.labels, g.labels ?? [])),
          );
    const groupPlace = (group: WorkspaceGroup) =>
      [
        ...(group.section_id ? [pathLabel(sectionItems, group.section_id)] : []),
        ...groupPath(groupItems, group.parent_group_id).map((g) => g.name),
      ].join(' / ');
    if (flat.length === 0 && matchingSections.length === 0 && matchingGroups.length === 0) {
      return (
        <p className={styles.noMatches} role="status">
          {activeFilterCount(filters, offered) > 0
            ? t('catalog.noMatches')
            : t('workspace.objectsNoMatches')}
        </p>
      );
    }
    return (
      <>
        {matchingSections.length > 0 && (
          <section aria-label={t('sections.searchResults')} className={styles.sectionHits}>
            <h3 className={styles.sectionHitsTitle}>{t('sections.searchResults')}</h3>
            <ul>
              {matchingSections.map((section) => (
                <li key={section.id}>
                  <button
                    type="button"
                    data-testid="section-hit"
                    onClick={() => {
                      onFocus(section.id);
                    }}
                  >
                    <Icons.folder /> <strong>{section.name}</strong>
                    <span className={styles.hitPath}>
                      {pathLabel(sectionItems, section.parent_id)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
        {matchingGroups.length > 0 && (
          <section aria-label={t('groups.searchResults')} className={styles.sectionHits}>
            <h3 className={styles.sectionHitsTitle}>{t('groups.searchResults')}</h3>
            <ul>
              {matchingGroups.map((group) => (
                <li key={group.id}>
                  <button
                    type="button"
                    data-testid="group-hit"
                    onClick={() => {
                      onOpenGroup(group.id, group.section_id);
                    }}
                  >
                    <Icons.group /> <strong>{group.name}</strong>
                    <span className={styles.hitPath}>{groupPlace(group)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
        {flat.length > 0 && (
          <ul className={styles.objectGrid} data-testid="workspace-objects">
            {flat.map((item) => item.node)}
          </ul>
        )}
        {dialogs}
      </>
    );
  }

  if (openGroup) {
    const inside = contentsOf(openGroup);
    const openSection = openGroup.section_id
      ? (findNode(roots, openGroup.section_id) ?? null)
      : null;
    return (
      <div data-testid="group-view">
        <Breadcrumbs
          sections={sectionItems}
          focus={openSection}
          onFocus={(id) => {
            onOpenGroup(null, id);
          }}
          groups={groupPath(groupItems, openGroup.id)}
          onOpenGroup={(id) => {
            onOpenGroup(id, openGroup.section_id);
          }}
        />
        {inside.length > 0 ? (
          <ul className={styles.objectGrid} data-testid="group-contents">
            {inside}
          </ul>
        ) : (
          <EmptyState title={t('groups.empty')}>
            <p>{t('groups.emptyHint')}</p>
          </EmptyState>
        )}
        <OutOfGroupDrop
          workspaceId={workspaceId}
          onDrop={(source) => {
            moveOutOf(openGroup, source);
          }}
        />
        {dialogs}
      </div>
    );
  }

  return (
    <>
      {focus && <Breadcrumbs sections={sectionItems} focus={focus} onFocus={onFocus} />}
      <SectionTree
        roots={roots}
        focus={focus}
        shared={{
          workspaceId,
          all: sectionItems,
          maxDepth,
          canManage,
          collapsed,
          resources: resourcesOf,
          onDropResource,
          onDropSection,
          onDropGroup,
          onFocus,
          onToggle: toggle,
          onCreateSub: (parent) => {
            setNaming({ parent });
          },
          onRename: (section) => {
            setNaming({ parent: null, section });
          },
          onMoveSection: (section) => {
            setMoving({
              kind: 'section',
              id: section.id,
              name: section.name,
              sectionId: section.parent_id,
            });
          },
          onDelete: setDeleting,
        }}
      />
      {dialogs}
    </>
  );
}
